import firebase from "../../../../services/firebase/FirebaseService";
import { store } from "../../../../state/configureStore";
import { addBook, removeBook, startLoading, finishLoading } from "./StudentHomeScreenState";
import LogService from "../../../../services/LogService";
import UploadService from "../../../../services/upload/UploadService";

class StudentHomeScreenService {
  cleanup: Array<() => void> = [];
  classroomWatches: Record<string, () => void> = {};

  startWatch(studentId: string, wordsPerMinute: number) {
    // First run there's no user id, so this needs to run in a different way

    startLoading();

    // Watch every classroom this student is a part of
    this.cleanup.push(
      firebase.watchStudentClassrooms(studentId, async classrooms => {
        // Track the number of watches that we're waiting on to start
        if (classrooms.length === 0) {
          finishLoading();
          return;
        }

        const existingBooks = store.getState().studentHomeScreenState.studentBooks;

        // Clean out books if we dropped a class
        const currentClassIds = classrooms.filter(obj => obj).map(obj => obj.id);
        for (const bookId of Object.keys(existingBooks)) {
          const book = existingBooks[bookId];
          if (book && !currentClassIds.includes(book.classId)) {
            removeBook(book.bookId);
          }
        }

        let activeWatches = 0;
        const registerWatch = () => (activeWatches += 1);
        const clearWatch = () => {
          activeWatches -= 1;
          if (activeWatches <= 0) {
            finishLoading();
          }
        };

        // Watch the assignments for each classroom to ensure that we see students being added/removed

        for (const classroom of classrooms) {
          // If this watch doesn't exist, start a new one
          if (!Object.keys(this.classroomWatches).includes(classroom.id)) {
            // Register classroom watch
            registerWatch();

            this.classroomWatches[classroom.id] = firebase.watchClassroomAssignments(
              classroom.id,
              async classroomAssignmentDetails => {
                for (const classroomAssignmentDetail of classroomAssignmentDetails) {
                  // If this assignment is assigned to this student, then we need to watch that assignment
                  if (classroomAssignmentDetail.students && classroomAssignmentDetail.students[studentId] === true) {
                    // Register the individual assignment watch
                    registerWatch();

                    this.cleanup.push(
                      firebase.watchAssignment(classroomAssignmentDetail.id, async (assignmentId, assignment) => {
                        if (assignment) {
                          // Register the submission watch
                          registerWatch();

                          this.cleanup.push(
                            firebase.watchSubmission(studentId, assignment.bookId, async submission => {
                              const book = await firebase.getBook(assignment.bookId);
                              const pages = await firebase.getPages(assignment.bookId);
                              let words: string[] = [];

                              if (book.coverImage) {
                                book.coverImageUrl = await UploadService.getCoverImagePath(book.coverImage);
                              } else {
                                book.coverImageUrl = undefined;
                              }

                              for (const page of pages) {
                                if (page.content) {
                                  const cleanedWords = page.content
                                    .replace(/^[A-Za-z0-9 -]/, "")
                                    .split(/(\b[^\s]+\b)/)
                                    .filter(obj => obj !== " ");
                                  words = words.concat(cleanedWords);
                                }
                              }

                              const distinctWords = new Set(words);

                              try {
                                addBook(assignment.id, {
                                  isNew: false,
                                  bookId: assignment.bookId,
                                  assignmentId: assignment.id,
                                  bookName: assignment.title || book.name,
                                  viewType: book.viewType,
                                  className: classroom.className || "",
                                  classId: classroom.id,
                                  wordCount: words.length,
                                  uniqueWordCount: distinctWords.size,
                                  completion: submission?.completionPercentage || 0,
                                  wordsPerMinute: submission ? submission.wordsPerMin : 0,
                                  accuracy: submission ? submission.accuracy : 0,
                                  coverImageUrl: book.coverImageUrl,
                                  // Make the estimated be 110% of their reading rate to give them
                                  // some wiggle room to feel like they're doing better than expected.
                                  estimatedTime:
                                    (words.length / (wordsPerMinute && !isNaN(wordsPerMinute) ? wordsPerMinute : 125)) *
                                    1.1,
                                  studentFacingData: assignment.studentFacingData,
                                  turnedIn: submission?.turnedIn || false,
                                  actualTime: submission?.totalTime,
                                });
                              } catch (err) {
                                LogService.exception(
                                  err,
                                  `StudentHomeScreenService.startWatch`,
                                  "Error adding the book",
                                );
                              }

                              // Clear the submission watch
                              clearWatch();
                            }),
                          );
                        } else {
                          // If there's no assignment, remove it if it's in our collection
                          removeBook(assignmentId);
                        }

                        // Clear the individual assignment watch
                        clearWatch();
                      }),
                    );
                  }
                }

                // Clear the classroom watch
                clearWatch();
              },
            );
          }
        }
      }),
    );
  }

  endWatch() {
    for (const cleanup of this.cleanup) {
      cleanup();
    }
    this.cleanup = [];
    this.cleanupClassroomWatches();
  }

  cleanupClassroomWatches() {
    for (const classroomId in Object.keys(this.classroomWatches)) {
      this.cleanupClassroomWatch(classroomId);
    }
  }

  cleanupClassroomWatch(classroomId: string) {
    this.classroomWatches?.[classroomId]?.();
    delete this.classroomWatches[classroomId];
  }
}

export default StudentHomeScreenService;
